

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewGuideAllocationModal from 'components/NewGuideAllocationModal';

const GuideAllocations = () => {
    const [allocations, setAllocations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('guide_allocations');

    useEffect(() => {
        fetchAllocations();
    }, []);

    const fetchAllocations = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/guide-allocations/');
            setAllocations(response.data);
        } catch (error) {
            console.error('Error fetching guide allocations:', error);
            setSnackbar({ open: true, message: 'Error loading guide allocations', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveAllocation = async (payload) => {
        try {
            await axiosInstance.post('/guide-allocations/', payload);
            setSnackbar({ open: true, message: 'Guide Allocation created successfully', severity: 'success' });
            fetchAllocations();
        } catch (error) {
            console.error('Error saving guide allocation:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/guide-allocation/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value || `Allocation #${row.original.id}`}
                </MDTypography>
            )
        },
        {
            Header: "Trip",
            accessor: "trip_id",
            width: "15%",
            Cell: ({ row }) => row.original.trip?.name || row.original.trip_id || '-'
        },
        {
            Header: "Guide",
            accessor: "guide_id",
            width: "15%",
            Cell: ({ row }) => row.original.guide?.name || row.original.guide_id || '-'
        },
        {
            Header: "Status",
            accessor: "status",
            width: "12%"
        },
        {
            Header: "Trip Compensation",
            accessor: "trip_compensation",
            width: "15%",
            Cell: ({ value }) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
        },
        {
            Header: "Travel Expense",
            accessor: "travel_expense",
            width: "12%",
            Cell: ({ value }) => value ? 'Yes' : 'No'
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "16%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: allocations,
    }), [allocations]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Guide Allocations</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Allocation
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : allocations.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                        onRowClick={handleRowClick}
                                    />
                                ) : (
                                    <EmptyState moduleName="Guide Allocation" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewGuideAllocationModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveAllocation}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default GuideAllocations;

